import {FC, useEffect, useState, useCallback} from 'react'
import {api, ServiceCategory, ServiceGroup, ServiceItem, ServiceFeature} from '../../services/api'
import {toast} from '../../utils/toast'

// Which flow an item belongs to. A radio, not a pair of checkboxes: an item is
// either a service under a Home Service sub-category or a row in the pickup
// list, never both and never neither.
const FlowRadio: FC<{
  checked: boolean
  onChange: () => void
  title: string
  hint: string
}> = ({checked, onChange, title, hint}) => (
  <label style={{
    display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, cursor: 'pointer',
    padding: '12px 14px', borderRadius: 8,
    border: `1px solid ${checked ? '#bfdbfe' : '#e2e8f0'}`,
    background: checked ? '#eff6ff' : '#f8fafc',
  }}>
    <input type='radio' checked={checked} onChange={onChange} style={{marginTop: 2, cursor: 'pointer'}} />
    <span>
      <span style={{display: 'block', fontSize: 13, fontWeight: 600, color: '#374151'}}>{title}</span>
      <span style={{display: 'block', fontSize: 11, color: '#94a3b8', marginTop: 2}}>{hint}</span>
    </span>
  </label>
)

// Names the flow a row belongs to. Both are labelled, not just Home Service:
// an unbadged row reads as one the flag was never set on, when in fact every
// row is one or the other. Same colours as the Sub Categories page.
const FlowBadge: FC<{home: boolean}> = ({home}) => (
  <span style={{
    marginLeft: 6, padding: '1px 7px', borderRadius: 999,
    fontSize: 10, fontWeight: 700, verticalAlign: 'middle',
    color: home ? '#0369a1' : '#4338ca',
    background: home ? '#e0f2fe' : '#e0e7ff',
  }}>
    {home ? 'HOME' : 'PICKUP'}
  </span>
)

const Toggle: FC<{checked: boolean; onChange: () => void}> = ({checked, onChange}) => (
  <div onClick={onChange} style={{width: 40, height: 22, borderRadius: 11, cursor: 'pointer', flexShrink: 0, background: checked ? '#22c55e' : '#e2e8f0', position: 'relative', transition: 'background 0.2s'}}>
    <div style={{position: 'absolute', top: 3, left: checked ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s'}} />
  </div>
)

const input = {width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#f8fafc', outline: 'none', boxSizing: 'border-box' as const}
const lbl = {fontSize: 13, fontWeight: 600 as const, color: '#374151', display: 'block' as const, marginBottom: 6}
const card = {background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)'}

const ServiceItemsPage: FC = () => {
  const [categories, setCategories] = useState<ServiceCategory[]>([])
  const [categoryId, setCategoryId] = useState('')
  // '' is both flows; 'home' the Home Service services, 'pickup' the shop list.
  const [flowFilter, setFlowFilter] = useState<'' | 'home' | 'pickup'>('')
  const [items, setItems] = useState<ServiceItem[]>([])
  const [groups, setGroups] = useState<ServiceGroup[]>([])
  const [loading, setLoading] = useState(false)

  const category = categories.find(c => c.id === categoryId)
  const isPackages = category?.layout === 'packages'

  // '' is "All categories" — the default, so the page opens showing everything
  // rather than whichever category happened to sort first.
  useEffect(() => {
    api.getServiceCategories().then(setCategories).catch(() => {})
  }, [])

  // Every category's sub-categories, not just the filtered one — the modal can
  // file an item under any category, so it needs them all to choose from.
  const reload = useCallback(() => {
    setLoading(true)
    Promise.all([
      api.getServiceItems(
        categoryId || undefined,
        flowFilter === '' ? undefined : flowFilter === 'home',
      ),
      api.getServiceGroups(),
    ])
      .then(([its, grs]) => { setItems(its); setGroups(grs) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [categoryId, flowFilter])
  useEffect(() => { reload() }, [reload])

  // ── Item modal ────────────────────────────────────────────────────────────
  const [showItem, setShowItem] = useState(false)
  const [editItem, setEditItem] = useState<ServiceItem | null>(null)
  const [saving, setSaving] = useState(false)
  const [fCategoryId, setFCategoryId] = useState('')
  const [fName, setFName] = useState('')
  const [fSubtitle, setFSubtitle] = useState('')
  const [fAddress, setFAddress] = useState('')
  const [fGroupId, setFGroupId] = useState('')
  const [fOrder, setFOrder] = useState('')
  const [fFeatures, setFFeatures] = useState<ServiceFeature[]>([])
  const [fIsHome, setFIsHome] = useState(false)
  const [fErr, setFErr] = useState('')

  // What the modal is editing against — its own category, not the table filter.
  const formCategory = categories.find(c => c.id === fCategoryId)
  const formIsPackages = formCategory?.layout === 'packages'
  const formGroups = groups.filter(g => g.categoryId === fCategoryId)

  const openAddItem = () => {
    setEditItem(null)
    // Deliberately blank rather than inheriting the table filter: the filter
    // narrows the table, it does not decide where a new item belongs.
    setFCategoryId('')
    setFName(''); setFSubtitle(''); setFAddress(''); setFGroupId(''); setFOrder('')
    setFFeatures([])
    // The one thing the filter does decide: while looking at the Home Service
    // list, "add" plainly means adding to it.
    setFIsHome(flowFilter === 'home')
    setFErr(''); setShowItem(true)
  }
  const openEditItem = (it: ServiceItem) => {
    setEditItem(it)
    setFCategoryId(it.categoryId)
    setFName(it.name); setFSubtitle(it.subtitle ?? ''); setFAddress(it.address ?? '')
    setFGroupId(it.groupId ?? ''); setFOrder(String(it.order))
    setFFeatures(it.features ?? [])
    setFIsHome(it.isHomeService)
    setFErr(''); setShowItem(true)
  }

  // Switching category changes which fields apply, and always invalidates the
  // sub-category, which belongs to the category left behind.
  const changeFormCategory = (id: string) => {
    setFCategoryId(id)
    setFGroupId('')
    const packages = categories.find(c => c.id === id)?.layout === 'packages'
    setFFeatures(packages ? (fFeatures.length ? fFeatures : [{text: '', included: true}]) : [])
  }

  const saveItem = async () => {
    if (!fName.trim()) { setFErr('Name is required.'); return }
    if (!fCategoryId) { setFErr('Pick a category.'); return }
    setSaving(true)
    try {
      // A Home Service row is one line a customer picks from a sub-category's
      // list. The pickup list's package checklist does not apply to it.
      const features = !fIsHome && formIsPackages ? fFeatures.filter(f => f.text.trim()) : undefined
      if (editItem) {
        await api.updateServiceItem(editItem.id, {
          name: fName.trim(), subtitle: fSubtitle, address: fAddress,
          groupId: fGroupId || null, features, isHomeService: fIsHome,
          order: fOrder ? parseInt(fOrder) : editItem.order,
        })
      } else {
        await api.createServiceItem({
          categoryId: fCategoryId, name: fName.trim(), subtitle: fSubtitle, address: fAddress,
          groupId: fGroupId || null, features, isHomeService: fIsHome,
          // items.length only counts the filtered category, so it is a valid
          // "put it last" only when the item is landing in that same category.
          order: fOrder ? parseInt(fOrder) : (fCategoryId === categoryId ? items.length : 0),
        })
      }
      setShowItem(false)
      // Follow the item to wherever it landed, so creating into another
      // category does not look like nothing happened.
      if (!editItem && categoryId && fCategoryId !== categoryId) setCategoryId(fCategoryId)
      else reload()
      toast(editItem ? 'Item updated' : 'Item created')
    } catch (e: any) { setFErr(e.message ?? 'Failed to save'); toast(e?.message ?? 'Save failed', 'error') }
    finally { setSaving(false) }
  }
  const toggleItem = async (it: ServiceItem) => {
    try { await api.updateServiceItem(it.id, {isActive: !it.isActive}); reload() } catch (e: any) { toast(e?.message ?? 'Failed', 'error') }
  }
  const deleteItem = async (it: ServiceItem) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return
    try { await api.deleteServiceItem(it.id); reload(); toast('Item deleted') } catch (e: any) { toast(e?.message ?? 'Delete failed', 'error') }
  }

  // Sub-categories themselves are created and edited on the Sub Categories
  // page; here they are only something an item can be filed under.
  const groupName = (id?: string | null) => groups.find(g => g.id === id)?.label

  return (
    <div>
      <div style={{marginBottom: 24}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Service Items</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>The service list under each category in the mobile app, and the services listed under each Home Service sub-category</p>
      </div>

      {/* Table filter only — a new item's category is picked in the modal. */}
      <div style={{...card, padding: '16px 20px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap'}}>
        <label style={{fontSize: 13, fontWeight: 600, color: '#374151'}}>Filter by category</label>
        <select value={categoryId} onChange={e => setCategoryId(e.target.value)} style={{...input, width: 'auto', minWidth: 220, flex: '0 1 auto'}}>
          <option value=''>All categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.label} ({c.layout})</option>)}
        </select>
        <label style={{fontSize: 13, fontWeight: 600, color: '#374151'}}>Flow</label>
        <select value={flowFilter} onChange={e => setFlowFilter(e.target.value as '' | 'home' | 'pickup')} style={{...input, width: 'auto', minWidth: 180, flex: '0 1 auto'}}>
          <option value=''>Both flows</option>
          <option value='pickup'>Pickup list</option>
          <option value='home'>Home Service</option>
        </select>
        <div style={{flex: 1}} />
        <button onClick={openAddItem} disabled={!categories.length}
          style={{display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#fff', background: '#3b82f6', cursor: 'pointer'}}>
          + Add Item
        </button>
      </div>

      {/* Items table */}
      <div style={{...card, overflow: 'hidden'}}>
        {loading ? (
          <div style={{padding: '60px 0', textAlign: 'center'}}><div style={{width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto'}} /></div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                  {(!categoryId
                    ? ['NAME', 'CATEGORY', 'SUB CATEGORY', 'STATUS', 'ACTIONS']
                    : isPackages
                    ? ['NAME', 'FEATURES', 'STATUS', 'ACTIONS']
                    : ['NAME', 'GROUP', 'STATUS', 'ACTIONS']).map(h => (
                    <th key={h} style={{padding: '11px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={it.id} style={{borderBottom: i < items.length - 1 ? '1px solid #f1f5f9' : 'none'}}>
                    {!categoryId ? (
                      // Mixed layouts in one list: name the category each row
                      // belongs to, since the columns can no longer imply it.
                      <>
                        <td style={{padding: '14px 20px'}}><span style={{fontWeight: 700, color: '#0f172a'}}>{it.name}</span><FlowBadge home={it.isHomeService} />{it.subtitle && <span style={{display: 'block', fontSize: 12, color: '#94a3b8'}}>{it.subtitle}</span>}</td>
                        <td style={{padding: '14px 20px', color: '#64748b'}}>{categories.find(c => c.id === it.categoryId)?.label ?? '—'}</td>
                        <td style={{padding: '14px 20px', color: '#64748b'}}>{groupName(it.groupId) ?? '—'}</td>
                      </>
                    ) : isPackages ? (
                      <>
                        <td style={{padding: '14px 20px', fontWeight: 700, color: '#0f172a'}}>{it.name}<FlowBadge home={it.isHomeService} /></td>
                        <td style={{padding: '14px 20px', color: '#64748b'}}>{it.features?.filter(f => f.included).length ?? 0}/{it.features?.length ?? 0} included</td>
                      </>
                    ) : (
                      <>
                        <td style={{padding: '14px 20px'}}><span style={{fontWeight: 700, color: '#0f172a'}}>{it.name}</span><FlowBadge home={it.isHomeService} />{it.subtitle && <span style={{display: 'block', fontSize: 12, color: '#94a3b8'}}>{it.subtitle}</span>}</td>
                        <td style={{padding: '14px 20px', color: '#64748b'}}>{groupName(it.groupId) ?? '—'}</td>
                      </>
                    )}
                    <td style={{padding: '14px 20px'}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                        <Toggle checked={it.isActive} onChange={() => toggleItem(it)} />
                        <span style={{fontSize: 13, color: it.isActive ? '#16a34a' : '#94a3b8'}}>{it.isActive ? 'Active' : 'Off'}</span>
                      </div>
                    </td>
                    <td style={{padding: '14px 20px'}}>
                      <div style={{display: 'flex', gap: 8}}>
                        <button onClick={() => openEditItem(it)} style={{padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, color: '#374151', background: '#fff', cursor: 'pointer'}}>Edit</button>
                        <button onClick={() => deleteItem(it)} style={{padding: '6px 14px', border: '1px solid #fecaca', borderRadius: 7, fontSize: 13, color: '#ef4444', background: '#fff', cursor: 'pointer'}}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={5} style={{padding: '48px 20px', textAlign: 'center', color: '#94a3b8'}}>No items yet — add one.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Item modal */}
      {showItem && (
        <div onClick={() => setShowItem(false)} style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20}}>
          <div onClick={e => e.stopPropagation()} style={{background: '#fff', borderRadius: 16, width: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.15)'}}>
            <div style={{padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', position: 'sticky', top: 0, background: '#fff'}}>
              <h3 style={{fontSize: 18, fontWeight: 700, margin: 0}}>{editItem ? 'Edit Item' : 'Add Item'} <span style={{fontSize: 13, fontWeight: 500, color: '#94a3b8'}}>({formCategory?.label})</span></h3>
              <button onClick={() => setShowItem(false)} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 20}}>✕</button>
            </div>
            <div style={{padding: 24, display: 'flex', flexDirection: 'column', gap: 16}}>
              {fErr && <div style={{padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13}}>{fErr}</div>}

              <div>
                <label style={lbl}>Category <span style={{color: '#ef4444'}}>*</span></label>
                <select value={fCategoryId} onChange={e => changeFormCategory(e.target.value)}
                  disabled={!!editItem} style={{...input, opacity: editItem ? 0.6 : 1}}>
                  {!editItem && <option value=''>— Select a category —</option>}
                  {categories.map(c => <option key={c.id} value={c.id}>{c.label} ({c.layout})</option>)}
                </select>
                <div style={{fontSize: 11, color: '#94a3b8', marginTop: 6}}>
                  {editItem
                    ? 'Fixed after creation — the sub-category and layout belong to this category.'
                    : 'Decides which fields apply and which sub-categories are offered.'}
                </div>
              </div>

              {/* Which of the two flows this row belongs to. It decides the
                  rest of the form, so it sits directly under the category. */}
              <div>
                <label style={lbl}>Flow</label>
                <div style={{display: 'flex', gap: 10}}>
                  <FlowRadio
                    checked={fIsHome}
                    onChange={() => setFIsHome(true)}
                    title='Home Service'
                    hint='A service under a Home Service sub-category'
                  />
                  <FlowRadio
                    checked={!fIsHome}
                    onChange={() => setFIsHome(false)}
                    title='Pickup'
                    hint='A row in the category’s pickup list'
                  />
                </div>
                {fIsHome && !fGroupId && (
                  <div style={{fontSize: 11, color: '#b45309', marginTop: 6}}>
                    Pick a sub-category below — a Home Service row with none is not shown on any card.
                  </div>
                )}
              </div>

              {/* Directly under the category it narrows. Only categories that
                  actually have sub-categories get it — an empty select would
                  read as a missing step. */}
              {formGroups.length > 0 && (
                <div>
                  <label style={lbl}>Sub-category</label>
                  <select value={fGroupId} onChange={e => setFGroupId(e.target.value)} style={input}>
                    <option value=''>— None —</option>
                    {formGroups.map(g => <option key={g.id} value={g.id}>{g.label}{g.isActive ? '' : ' (disabled)'}</option>)}
                  </select>
                  <div style={{fontSize: 11, color: '#94a3b8', marginTop: 6}}>Managed on the Sub Categories page.</div>
                </div>
              )}

              <div><label style={lbl}>Name <span style={{color: '#ef4444'}}>*</span></label><input value={fName} onChange={e => setFName(e.target.value)} style={input} placeholder='e.g. Unleaded' /></div>
              <div><label style={lbl}>Subtitle</label><input value={fSubtitle} onChange={e => setFSubtitle(e.target.value)} style={input} placeholder='e.g. 87 Octane Rating' /></div>

              {/* Which fields apply is a property of the category, so nothing
                  layout-specific is shown until one is chosen. */}
              {fIsHome ? null : !fCategoryId ? (
                <div style={{padding: '12px 14px', background: '#f8fafc', border: '1px dashed #e2e8f0', borderRadius: 8, fontSize: 13, color: '#94a3b8'}}>
                  Pick a category to see its remaining fields.
                </div>
              ) : formIsPackages ? (
                <div>
                  <label style={lbl}>Features</label>
                  {fFeatures.map((f, idx) => (
                    <div key={idx} style={{display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6}}>
                      <input value={f.text} onChange={e => setFFeatures(fs => fs.map((x, j) => j === idx ? {...x, text: e.target.value} : x))} placeholder='Feature' style={{...input, flex: 1}} />
                      <label style={{display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#64748b', whiteSpace: 'nowrap'}}>
                        <input type='checkbox' checked={f.included} onChange={e => setFFeatures(fs => fs.map((x, j) => j === idx ? {...x, included: e.target.checked} : x))} /> incl.
                      </label>
                      <button onClick={() => setFFeatures(fs => fs.filter((_, j) => j !== idx))} style={{border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer'}}>✕</button>
                    </div>
                  ))}
                  <button onClick={() => setFFeatures(fs => [...fs, {text: '', included: true}])} style={{marginTop: 4, padding: '7px 12px', border: '1px dashed #cbd5e1', borderRadius: 8, background: '#f8fafc', fontSize: 13, cursor: 'pointer', color: '#475569'}}>+ Add feature</button>
                </div>
              ) : (
                <div><label style={lbl}>Address</label><input value={fAddress} onChange={e => setFAddress(e.target.value)} style={input} placeholder='e.g. Jl. …, (3.2 KM near you)' /></div>
              )}

              <div><label style={lbl}>Display Order</label><input type='number' value={fOrder} onChange={e => setFOrder(e.target.value)} style={input} /></div>
            </div>
            <div style={{padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10, position: 'sticky', bottom: 0, background: '#fff'}}>
              <button onClick={() => setShowItem(false)} disabled={saving} style={{padding: '9px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 14, cursor: 'pointer', color: '#374151'}}>Cancel</button>
              <button onClick={saveItem} disabled={saving} style={{padding: '9px 20px', borderRadius: 8, border: 'none', background: '#3b82f6', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#fff', opacity: saving ? 0.7 : 1}}>{saving ? 'Saving…' : editItem ? 'Save Changes' : 'Add Item'}</button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

const ServiceItemsPageWrapper: FC = () => <ServiceItemsPage />
export {ServiceItemsPageWrapper}
